import { useMemo, useState } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Select } from '@magnetic/select'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Tag } from '@magnetic/tag'
import { Skeleton } from '@magnetic/skeleton'
import { Table, useMagneticTable, type MdsTableColumn } from '@magnetic/table'
import { Tooltip } from '@magnetic/tooltip'
import {
  Warning,
  ShieldCheck,
  ArrowsClockwise,
  Package
} from '@/lib/icons'
import { getVulnerableImages } from '@/lib/api'

interface VulnerableImage {
  repositoryName: string
  imageDigest: string
  imageTags: string[]
  pushedAt: string
  scanFindings: {
    criticalCount: number
    highCount: number
    mediumCount: number
    lowCount: number
    informationalCount: number
    totalFindings: number
    scanStatus: string
    scanCompletedAt: string
  }
}

interface VulnerableImageRow extends VulnerableImage {
  id: string
}

export function ECRVulnerableImages() {
  const [minSeverity, setMinSeverity] = useState<string>('CRITICAL')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: imagesResponse, isLoading, error, refetch } = useConnectQuery(
    getVulnerableImages,
    { minSeverity },
    {
      refetchInterval: 120000,
    }
  )

  const images = imagesResponse?.images as VulnerableImage[] | undefined

  const formatDate = (timestamp: string) => {
    if (!timestamp) return 'Unknown'
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSeverityBadge = (findings: VulnerableImage['scanFindings']) => {
    if (findings.criticalCount > 0) {
      return <Tag.Status status="negative">Critical</Tag.Status>
    }
    if (findings.highCount > 0) {
      return <Tag.Status status="severe-warning">High</Tag.Status>
    }
    if (findings.mediumCount > 0) {
      return <Tag.Status status="warning">Medium</Tag.Status>
    }
    return <Tag.Status status="inactive">Low</Tag.Status>
  }

  const filteredImages = useMemo(() => (images?.filter(img =>
    img.repositoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.imageTags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || []), [images, searchTerm])

  const tableData: VulnerableImageRow[] = useMemo(() => filteredImages.map((img) => ({
    ...img,
    id: img.imageDigest,
  })), [filteredImages])

  const columns: MdsTableColumn<VulnerableImageRow>[] = useMemo(() => [
    {
      id: 'repository',
      header: 'Repository',
      accessorFn: (row) => row.repositoryName,
      cell: ({ row }) => (
        <Flex align="center" gap="sm">
          <Package size={16} style={{ opacity: 0.5 }} />
          <Flex direction="vertical" gap="xs">
            <Text size="sm" weight="semi-bold">{row.original.repositoryName}</Text>
            <Tag>{row.original.imageDigest.split(':')[1]?.slice(0, 12)}</Tag>
          </Flex>
        </Flex>
      ),
    },
    {
      id: 'tags',
      header: 'Tags',
      accessorFn: (row) => row.imageTags.join(','),
      cell: ({ row }) => (
        <Flex wrap gap="xs">
          {row.original.imageTags.slice(0, 3).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {row.original.imageTags.length > 3 && (
            <Tag>+{row.original.imageTags.length - 3}</Tag>
          )}
        </Flex>
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      accessorFn: (row) => row.scanFindings.criticalCount + row.scanFindings.highCount + row.scanFindings.mediumCount + row.scanFindings.lowCount,
      cell: ({ row }) => getSeverityBadge(row.original.scanFindings),
    },
    {
      id: 'findings',
      header: 'Findings',
      accessorFn: (row) => row.scanFindings.totalFindings,
      cell: ({ row }) => (
        <Flex align="center" gap="sm">
          {row.original.scanFindings.criticalCount > 0 && (
            <Tag.Status status="negative">{`${row.original.scanFindings.criticalCount}C`}</Tag.Status>
          )}
          {row.original.scanFindings.highCount > 0 && (
            <Tag.Status status="severe-warning">{`${row.original.scanFindings.highCount}H`}</Tag.Status>
          )}
          {row.original.scanFindings.mediumCount > 0 && (
            <Tag.Status status="warning">{`${row.original.scanFindings.mediumCount}M`}</Tag.Status>
          )}
          {row.original.scanFindings.lowCount > 0 && (
            <Tag.Status status="inactive">{`${row.original.scanFindings.lowCount}L`}</Tag.Status>
          )}
        </Flex>
      ),
    },
    {
      id: 'scanned',
      header: 'Scanned',
      accessorFn: (row) => row.scanFindings.scanCompletedAt,
      cell: ({ row }) => (
        <Text size="xs" color="light">
          {formatDate(row.original.scanFindings.scanCompletedAt)}
        </Text>
      ),
    },
  ], [])

  const { table } = useMagneticTable({
    data: tableData,
    columns,
  })

  const severityOptions = [
    { value: 'CRITICAL', label: 'Critical Only' },
    { value: 'HIGH', label: 'High & Above' },
    { value: 'MEDIUM', label: 'Medium & Above' },
    { value: 'LOW', label: 'All Severities' },
  ]

  if (isLoading) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ShieldCheck size={20} />
            <Heading size="sub-section">Vulnerable Images</Heading>
          </Flex>
          <Flex direction="vertical" gap="sm" style={{ padding: 8 }}>
            <Skeleton height="md" shade="dark" style={{ width: '30%' }} />
            <Skeleton height="sm" style={{ width: '100%' }} />
            <Skeleton height="sm" style={{ width: '100%' }} />
            <Skeleton height="sm" style={{ width: '100%' }} />
            <Skeleton height="sm" style={{ width: '100%' }} />
          </Flex>
        </Flex>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ShieldCheck size={20} />
            <Heading size="sub-section">Vulnerable Images</Heading>
          </Flex>
          <Flex direction="vertical" align="center" gap="md" style={{ padding: 32 }}>
            <Warning size={32} style={{ color: 'var(--mds-color-theme-text-error-normal)' }} />
            <Text size="sm" color="light">
              {error instanceof Error ? error.message : 'Failed to load vulnerable images'}
            </Text>
            <Button kind="secondary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />}>
              Retry
            </Button>
          </Flex>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="sm">
            <ShieldCheck size={20} />
            <Heading size="sub-section">Vulnerable Images</Heading>
          </Flex>
          <Tooltip title="Refresh vulnerabilities">
            <Button kind="tertiary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />} />
          </Tooltip>
        </Flex>

        <Flex gap="md" wrap>
          <Flex style={{ flex: 1, minWidth: 200 }}>
            <Input
              label="Search"
              hiddenLabel
              placeholder="Search repositories or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Flex>
          <Flex style={{ width: 180 }}>
            <Select
              value={minSeverity}
              onChange={(value) => setMinSeverity(value as string)}
              options={severityOptions}
            />
          </Flex>
        </Flex>

        {filteredImages.length === 0 ? (
          <Flex direction="vertical" align="center" style={{ padding: 32 }}>
            <ShieldCheck size={32} style={{ color: 'var(--mds-color-theme-text-success-normal)' }} />
            <Text size="sm" color="light">
              No vulnerable images found with {minSeverity.toLowerCase()} severity
            </Text>
          </Flex>
        ) : (
          <Table table={table} />
        )}
      </Flex>
    </Card>
  )
}
